import { RefObject, useEffect, useRef, useState } from "react";

export function usePrevious<T>(value: T) {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useDebounce<T>(value: T, delay: number = 0) {
  const [ debouncedValue, setDebouncedValue ] = useState<T>(value);

  useEffect(
    () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    },
    [ delay, value ],
  );

  return debouncedValue;
}

export function useDelayHint(loading: boolean, delay: number, delayFor = true) {
  const [ delayed, setDelayed ] = useState(loading);
  const timer = useRef<any>(null);
  useEffect(() => {
    if (loading === delayFor) {
      timer.current = setTimeout(() => setDelayed(delayFor), delay);
    } else {
      setDelayed(!delayFor);
    }
    return () => clearTimeout(timer.current);
  }, [ delay, loading, delayFor ]);

  return delayed;
}

export const useInterval = (callback: (...args: any) => void, delay: number) => {
  const savedCallback = useRef<any>(null);

  useEffect(
    () => {
      savedCallback.current = callback;
    },
    [ callback ],
  );

  useEffect(
    () => {
      const handler = (...args: any) => savedCallback.current?.(...args);

      if (delay !== null) {
        const id = setInterval(handler, delay);
        return () => clearInterval(id);
      }
    },
    [ delay ],
  );
};

export function useTimeout(callback: () => void, timeout?: number, disabled?: boolean) {
  const timerProps = useRef<{
    callback: () => void,
    disabled?: boolean,
  } | undefined>(undefined);

  useEffect(() => {
    timerProps.current = { callback, disabled };
  }, [ callback, disabled ]);


  useEffect(() => {
    if (!disabled && timeout) {
      const timerId = setTimeout(() => {
        if (!timerProps.current?.disabled) {
          timerProps.current?.callback?.();
        }
      }, timeout);
      return () => clearTimeout(timerId);
    }
  }, [ timerProps, disabled, timeout ]);
}

export function useOutsideClick<T extends HTMLElement>(ref: RefObject<T | null> | undefined, callback: (() => void) | undefined) {
  const handleClick = (e: MouseEvent) => {
    if (ref?.current && !ref?.current?.contains?.(e.target as Node)) {
      callback?.();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
}
